import { type Bone, type Skeleton } from 'three'

/**
 * A snapshot of every bone's local rotation (and optionally position) at a single instant
 * in time. Produced from a `Skeleton` by `PoseSnapshot.capture()` and consumed by
 * `KeyframeBuilder.upsert_pose_keyframe()` to bake into an `AnimationClip`.
 *
 * Rotations are stored as `[x, y, z, w]` quaternion tuples to match Three.js's
 * `QuaternionKeyframeTrack` value layout, which lets us write them into Float32Arrays
 * with no further conversion.
 */
export interface PoseBoneSnapshot {
  bone_name: string
  /** Local-space quaternion as [x, y, z, w]. */
  quaternion: [number, number, number, number]
  /**
   * Local-space position as [x, y, z]. Only included for bones in `position_bone_names`
   * — typically just the root motion bone (e.g. `pelvis` or `head` for snake) since
   * rest-of-skeleton position tracks don't exist in the Mesh2Motion pipeline.
   */
  position?: [number, number, number]
}

export interface PoseSnapshot {
  /** Time on the playhead when this pose was captured, in seconds. */
  time: number
  bones: PoseBoneSnapshot[]
}

export interface CaptureOptions {
  /**
   * Bones whose local-space position should be captured in addition to the rotation.
   * Pass the rig's `position_tracking_bone_name` (from `RigConfig`) — typically
   * a single-element array like `['pelvis']`. Empty by default.
   */
  position_bone_names?: string[]
}

export class PoseSnapshotUtil {
  /**
   * Captures the local-space rotation of every bone in the skeleton at the given playhead
   * time. Optionally captures position for bones named in `options.position_bone_names`.
   * Pure read-only operation: does not modify the skeleton.
   */
  static capture (skeleton: Skeleton, time: number, options: CaptureOptions = {}): PoseSnapshot {
    const position_set = new Set(options.position_bone_names ?? [])
    const bones: PoseBoneSnapshot[] = skeleton.bones.map((bone: Bone) => {
      const snapshot: PoseBoneSnapshot = {
        bone_name: bone.name,
        quaternion: [bone.quaternion.x, bone.quaternion.y, bone.quaternion.z, bone.quaternion.w]
      }
      if (position_set.has(bone.name)) {
        snapshot.position = [bone.position.x, bone.position.y, bone.position.z]
      }
      return snapshot
    })
    return { time, bones }
  }
}
